import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RootState } from '../../store/store';
import { useStartImpersonationMutation } from './api/impersonationApi';
import {
  type MemberListItem,
  type MemberSortBy,
  type SortDir,
  useListMembersQuery,
} from './api/membersApi';
import MemberOnboardingDialog from './components/MemberOnboardingDialog';
import { startAdminImpersonationSession } from './utils/adminImpersonation';

export default function MembersListPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentToken = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<MemberSortBy>('first_login');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [memberOnboardingOpen, setMemberOnboardingOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const per = 25;

  const { data, isLoading, isFetching, error, refetch } = useListMembersQuery({
    q: searchTerm || undefined,
    page,
    per,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const [startImpersonationApi, { isLoading: isImpersonating }] = useStartImpersonationMutation();

  const members = data?.members ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / per));

  const handleSort = (column: MemberSortBy) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir(['name', 'email'].includes(column) ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleRowClick = (memberId: string) => {
    navigate(`/admin/members/${memberId}`);
  };

  const handleViewLifecycle = (memberId: string) => {
    navigate(`/admin/members/${memberId}#lifecycle`);
  };

  const handleImpersonate = async (member: MemberListItem) => {
    if (!currentToken || !currentUser) {
      setSnackbar({
        open: true,
        message: 'Current admin session is unavailable.',
        severity: 'error',
      });
      return;
    }

    try {
      const result = await startImpersonationApi({ target_person_id: member.id }).unwrap();
      startAdminImpersonationSession({
        dispatch,
        navigate,
        currentToken,
        currentUser,
        result,
        returnPath: `${location.pathname}${location.search}`,
      });
    } catch (err) {
      const message =
        (err as any)?.data?.error_description ||
        (err as any)?.message ||
        'Failed to start impersonation.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 1125, margin: '0 auto', padding: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Admin Home / Members
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Members
        </Typography>
        {(isLoading || isFetching) && <CircularProgress size={20} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View members and impersonate their member experience.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load members. {(error as any)?.data?.error_description || 'Please try again.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email or name"
            size="small"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }}
            sx={{ minWidth: 250 }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
            Search
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<GroupAddOutlinedIcon />}
            onClick={() => setMemberOnboardingOpen(true)}
          >
            Onboard by email
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Page {page} of {totalPages} • {total} members
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'name' || sortBy === 'email'}
                    direction={sortBy === 'name' || sortBy === 'email' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'sessions'}
                    direction={sortBy === 'sessions' ? sortDir : 'desc'}
                    onClick={() => handleSort('sessions')}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'artifacts'}
                    direction={sortBy === 'artifacts' ? sortDir : 'desc'}
                    onClick={() => handleSort('artifacts')}
                  >
                    Artifacts
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'first_login'}
                    direction={sortBy === 'first_login' ? sortDir : 'desc'}
                    onClick={() => handleSort('first_login')}
                  >
                    First Login
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'last_active'}
                    direction={sortBy === 'last_active' ? sortDir : 'desc'}
                    onClick={() => handleSort('last_active')}
                  >
                    Last Active
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => {
                return (
                  <TableRow
                    key={member.id}
                    hover
                    onClick={() => handleRowClick(member.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{member.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{member.session_count}</TableCell>
                    <TableCell align="right">{member.artifact_count}</TableCell>
                    <TableCell>
                      {member.first_login_at
                        ? formatDistanceToNow(new Date(member.first_login_at), { addSuffix: true })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {member.last_active_at
                        ? formatDistanceToNow(new Date(member.last_active_at), { addSuffix: true })
                        : '-'}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View member lifecycle">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleViewLifecycle(member.id)}
                            aria-label={`View lifecycle for ${member.name}`}
                          >
                            <AccountTreeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="View as this member">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleImpersonate(member)}
                            disabled={isImpersonating}
                            aria-label={`View as ${member.name}`}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {members.length === 0 && !isLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No members found.</Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Showing {members.length} of {total}
        </Typography>
      </Box>

      <MemberOnboardingDialog
        open={memberOnboardingOpen}
        onClose={() => setMemberOnboardingOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
